import type { HomeOverviewDto } from '../../types/generated/models/home-overview-dto'
import type { HomeOverviewDtoApiResponse } from '../../types/generated/models/home-overview-dto-api-response'
import type { HomeRecentJobDto } from '../../types/generated/models/home-recent-job-dto'
import type { HomeRecentJobDtoListApiResponse } from '../../types/generated/models/home-recent-job-dto-list-api-response'
import type { HomeRecentDeviceDto } from '../../types/generated/models/home-recent-device-dto'
import type { HomeRecentDeviceDtoListApiResponse } from '../../types/generated/models/home-recent-device-dto-list-api-response'
import type { HomeJobDetailDto } from '../../types/generated/models/home-job-detail-dto'
import type { HomeJobDetailDtoApiResponse } from '../../types/generated/models/home-job-detail-dto-api-response'

/* ── State interface ── */

export interface HomeState {
  overview: HomeOverviewDto
  recentJobs: {
    data: HomeRecentJobDto[]
    pagination: {
      pageNumber: number
      pageSize: number
      totalCount: number
      totalPages: number
    }
    loading: boolean
    error: boolean | null
    errorMessage: string | null
  }
  recentDevices: {
    data: HomeRecentDeviceDto[]
    pagination: {
      pageNumber: number
      pageSize: number
      totalCount: number
      totalPages: number
    }
    loading: boolean
    error: boolean | null
    errorMessage: string | null
  }
  jobDetails: {
    data: HomeJobDetailDto | null
    loading: boolean
    error: boolean | null
    errorMessage: string | null
  }
  loading: boolean
  error: boolean | null
  errorMessage: string | null
}

/* ── Action types ── */

export const GET_HOME_OVERVIEW = 'GET_HOME_OVERVIEW'
export const GET_HOME_OVERVIEW_SUCCESS = 'GET_HOME_OVERVIEW_SUCCESS'
export const GET_HOME_OVERVIEW_FAIL = 'GET_HOME_OVERVIEW_FAIL'

export const GET_HOME_RECENT_JOBS = 'GET_HOME_RECENT_JOBS'
export const GET_HOME_RECENT_JOBS_SUCCESS = 'GET_HOME_RECENT_JOBS_SUCCESS'
export const GET_HOME_RECENT_JOBS_FAIL = 'GET_HOME_RECENT_JOBS_FAIL'

export const GET_HOME_RECENT_DEVICES = 'GET_HOME_RECENT_DEVICES'
export const GET_HOME_RECENT_DEVICES_SUCCESS = 'GET_HOME_RECENT_DEVICES_SUCCESS'
export const GET_HOME_RECENT_DEVICES_FAIL = 'GET_HOME_RECENT_DEVICES_FAIL'

export const GET_JOB_DETAILS = 'GET_JOB_DETAILS'
export const GET_JOB_DETAILS_SUCCESS = 'GET_JOB_DETAILS_SUCCESS'
export const GET_JOB_DETAILS_FAIL = 'GET_JOB_DETAILS_FAIL'

/* ── Initial state ── */

export const defaultHomeState: HomeState = {
  overview: {
    registeredDevices: 0,
    activeDevices: 0,
    jobsCompleted: 0,
    testsCompleted: 0,
    performedTests: 0,
    successRate: 0,
    failedTests: 0,
    alerts: 0
  },
  recentJobs: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0
    },
    loading: false,
    error: null,
    errorMessage: null
  },
  recentDevices: {
    data: [],
    pagination: {
      pageNumber: 1,
      pageSize: 10,
      totalCount: 0,
      totalPages: 0
    },
    loading: false,
    error: null,
    errorMessage: null
  },
  jobDetails: {
    data: null,
    loading: false,
    error: null,
    errorMessage: null
  },
  loading: false,
  error: null,
  errorMessage: null
}

/* ── Reducer ── */

export default (state: HomeState = defaultHomeState, action: any): HomeState => {
  switch (action.type) {
    case GET_HOME_OVERVIEW:
      return {
        ...state,
        loading: true,
        error: null
      }

    case GET_HOME_OVERVIEW_SUCCESS: {
      const response = action.payload.data as HomeOverviewDtoApiResponse
      if (response.success) {
        return {
          ...state,
          overview: response.data as HomeOverviewDto,
          loading: false,
          error: false,
          errorMessage: null
        }
      }
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: response.error ?? null
      }
    }

    case GET_HOME_OVERVIEW_FAIL:
      return {
        ...state,
        loading: false,
        error: true,
        errorMessage: action.error?.message || 'Failed to load overview'
      }

    case GET_HOME_RECENT_JOBS:
      return {
        ...state,
        recentJobs: {
          ...state.recentJobs,
          loading: true,
          error: null
        }
      }

    case GET_HOME_RECENT_JOBS_SUCCESS: {
      const response = action.payload.data as HomeRecentJobDtoListApiResponse & { pagination?: HomeState['recentJobs']['pagination'] }
      if (response.success) {
        return {
          ...state,
          recentJobs: {
            data: (response.data ?? []) as HomeRecentJobDto[],
            pagination: response.pagination ?? state.recentJobs.pagination,
            loading: false,
            error: false,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        recentJobs: {
          ...state.recentJobs,
          loading: false,
          error: true,
          errorMessage: response.error ?? null
        }
      }
    }

    case GET_HOME_RECENT_JOBS_FAIL:
      return {
        ...state,
        recentJobs: {
          ...state.recentJobs,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load recent jobs'
        }
      }

    case GET_HOME_RECENT_DEVICES:
      return {
        ...state,
        recentDevices: {
          ...state.recentDevices,
          loading: true,
          error: null
        }
      }

    case GET_HOME_RECENT_DEVICES_SUCCESS: {
      const response = action.payload.data as HomeRecentDeviceDtoListApiResponse & { pagination?: HomeState['recentDevices']['pagination'] }
      if (response.success) {
        return {
          ...state,
          recentDevices: {
            data: (response.data ?? []) as HomeRecentDeviceDto[],
            pagination: response.pagination ?? state.recentDevices.pagination,
            loading: false,
            error: false,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        recentDevices: {
          ...state.recentDevices,
          loading: false,
          error: true,
          errorMessage: response.error ?? null
        }
      }
    }

    case GET_HOME_RECENT_DEVICES_FAIL:
      return {
        ...state,
        recentDevices: {
          ...state.recentDevices,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load recent devices'
        }
      }

    case GET_JOB_DETAILS:
      return {
        ...state,
        jobDetails: {
          ...state.jobDetails,
          loading: true,
          error: null
        }
      }

    case GET_JOB_DETAILS_SUCCESS: {
      const response = action.payload.data as HomeJobDetailDtoApiResponse
      if (response.success) {
        return {
          ...state,
          jobDetails: {
            data: (response.data ?? null) as HomeJobDetailDto | null,
            loading: false,
            error: false,
            errorMessage: null
          }
        }
      }
      return {
        ...state,
        jobDetails: {
          ...state.jobDetails,
          loading: false,
          error: true,
          errorMessage: response.error ?? null
        }
      }
    }

    case GET_JOB_DETAILS_FAIL:
      return {
        ...state,
        jobDetails: {
          ...state.jobDetails,
          loading: false,
          error: true,
          errorMessage: action.error?.message || 'Failed to load job details'
        }
      }

    default:
      return state
  }
}
